import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { useComboboxData } from './useComboboxData';

// eslint_disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

const DEFAULT_PARAMS: Record<string, unknown> = {};

interface ItemTextProps {
  item: AnyRecord | null;
  labelKey: string;
  labelKeys?: string[];
  valueKey: string;
  renderLabel?: (item: AnyRecord) => string;
}

function getItemText({ item, labelKey, labelKeys, valueKey, renderLabel }: ItemTextProps): string {
  if (!item) return '';
  if (renderLabel) return renderLabel(item);
  if (labelKeys && labelKeys.length > 0) {
    return labelKeys
      .map((key) => item[key])
      .filter(Boolean)
      .join(' ');
  }
  const label = item[labelKey];
  const value = item[valueKey];
  return String(label ?? value ?? '');
}

function ItemText(props: ItemTextProps) {
  return <>{getItemText(props)}</>;
}

export interface UniversalComboboxProps {
  url: string;
  listUrl?: string;
  labelKey?: string;
  labelKeys?: string[];
  valueKey?: string;
  onSelectItem?: (item: AnyRecord | null) => void;
  placeholder?: string;
  queryKey?: string;
  params?: Record<string, unknown>;
  className?: string;
  selectedId?: number | string | undefined;
  defaultSelectedId?: number | string | undefined;
  disabled?: boolean;
  renderLabel?: (item: AnyRecord) => string;
  clientSideFilter?: boolean;
  /**
   * Fire `onSelectItem` when a preselected `selectedId` is resolved from the API.
   * Defaults to true (hydration echo). Set false when `onSelectItem` prefills
   * other fields — otherwise the echo overwrites the user's edits on load.
   */
  notifyOnPreselect?: boolean;
}

export function UniversalCombobox({
  url,
  listUrl,
  labelKey = 'name',
  labelKeys,
  valueKey = 'id',
  onSelectItem,
  placeholder = 'Select...',
  queryKey = 'comboData',
  params = DEFAULT_PARAMS,
  className = '',
  selectedId,
  defaultSelectedId,
  disabled = false,
  renderLabel,
  clientSideFilter = false,
  notifyOnPreselect = true,
}: UniversalComboboxProps) {
  const { t } = useTranslation();
  const listboxId = React.useId();
  const [open, setOpen] = React.useState(false);

  const {
    inputValue,
    setInputValue,
    items,
    isFetching,
    selectedItem,
    handleSelect,
    resetInput,
  } = useComboboxData({
    url,
    listUrl,
    valueKey,
    queryKey,
    params,
    selectedId,
    defaultSelectedId,
    open,
    disabled,
    onSelectItem,
    notifyOnPreselect,
  });

  const handleOpenChange = (state: boolean) => {
    if (disabled) return;
    setOpen(state);
    if (!state) {
      resetInput();
    }
  };

  const handleSelectItem = (item: AnyRecord | null) => {
    handleSelect(item);
    setOpen(false);
  };

  const displayedItems = clientSideFilter && inputValue.length > 0
    ? items.filter((item) =>
        getItemText({ item, labelKey, labelKeys, valueKey, renderLabel })
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      )
    : items;

  return (
    <Popover open={!disabled && open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          disabled={disabled}
          data-disabled={disabled ? '' : undefined}
          className={cn(
            'w-full min-w-0 justify-between overflow-hidden',
            disabled && 'pointer-events-none opacity-70 bg-slate-100 text-slate-500',
            className
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {selectedItem
              ? getItemText({ item: selectedItem, labelKey, labelKeys, valueKey, renderLabel })
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      {!disabled && (
        <PopoverContent className="w-[280px] p-0">
          <Command label={t('search') ?? 'Search'} shouldFilter={false}>
            <CommandInput
              placeholder={t('search') ?? 'Search...'}
              className="h-9"
              onValueChange={(v) => setInputValue(v ?? '')}
            />
            <CommandList id={listboxId}>
              <CommandEmpty>
                {isFetching
                  ? t('loading') ?? 'Loading...'
                  : !clientSideFilter && inputValue.length > 0 && inputValue.length < 2
                    ? t('need2Char') ?? 'Type at least 2 characters'
                    : t('noDataFound') ?? 'No results found'}
              </CommandEmpty>

              <CommandGroup>
                <CommandItem
                  value="__none__"
                  className="font-bold"
                  onSelect={() => handleSelectItem(null)}
                >
                  {placeholder}
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4',
                      !selectedItem ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>

                {displayedItems.map((it, i) => {
                  const idVal = (it[valueKey] ?? `row-${String(i)}`) as string | number;
                  const isActive =
                    selectedItem &&
                    String(selectedItem[valueKey]) === String(it[valueKey]);

                  return (
                    <CommandItem
                      key={String(idVal)}
                      value={String(idVal)}
                      onSelect={() => handleSelectItem(it)}
                    >
                      <ItemText
                        item={it}
                        labelKey={labelKey}
                        labelKeys={labelKeys}
                        valueKey={valueKey}
                        renderLabel={renderLabel}
                      />
                      <Check
                        className={cn(
                          'ml-auto h-4 w-4',
                          isActive ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
}
